import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import ReconWatchChip from '@/components/dashboard/ReconWatchChip';
import TallyExportReminder from '@/components/dashboard/TallyExportReminder';
import {
  IndianRupee, FileText, Receipt, CreditCard, BookOpen,
  CalendarClock, ArrowUpRight, AlertCircle, Megaphone,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const CLOSED_INVOICE = ['paid', 'cancelled', 'converted', 'void', 'voided'];
const CLOSED_LINK = ['paid', 'cancelled', 'expired', 'voided'];

const invoiceOutstanding = (inv) => Number(inv.totalAmount || 0) - Number(inv.amountPaid || 0);
const isOutstanding = (inv) =>
  !CLOSED_INVOICE.includes((inv.status || '').toLowerCase()) && invoiceOutstanding(inv) > 0.5;

const KpiCard = ({ icon: Icon, label, value, sub, testid, tone = 'teal' }) => {
  const tones = {
    teal: 'from-teal-500 to-emerald-600',
    amber: 'from-amber-500 to-orange-500',
    sky: 'from-sky-500 to-indigo-500',
    rose: 'from-rose-500 to-pink-600',
  };
  return (
    <Card data-testid={testid} className="p-5 bg-white rounded-2xl border border-slate-100 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${tones[tone]} flex items-center justify-center text-white shrink-0`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 truncate">{label}</div>
          <div className="text-2xl font-bold text-slate-900 leading-tight">{value}</div>
          {sub ? <div className="text-xs text-slate-400 truncate">{sub}</div> : null}
        </div>
      </div>
    </Card>
  );
};

const Panel = ({ title, icon: Icon, action, children, testid }) => (
  <Card data-testid={testid} className="p-5 bg-white rounded-2xl border border-slate-100 shadow-sm">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
        {Icon ? <Icon className="h-4 w-4 text-teal-600" /> : null}{title}
      </h3>
      {action}
    </div>
    {children}
  </Card>
);

const taxStatusColor = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'filed' || s === 'paid') return 'bg-emerald-100 text-emerald-700';
  if (s === 'overdue') return 'bg-red-100 text-red-700';
  return 'bg-amber-100 text-amber-700';
};

const AccountsDashboard = () => {
  const [invoices, setInvoices] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [links, setLinks] = useState([]);
  const [ledger, setLedger] = useState({ receivable: 0, payable: 0, net: 0 });
  const [deadlines, setDeadlines] = useState([]);
  const [marketingSpend, setMarketingSpend] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [inv, rec, lk, led, dl, summary] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/invoices`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/finance/receipts?limit=500`).catch(() => ({ data: {} })),
          axios.get(`${BACKEND_URL}/api/payments/links?limit=500`).catch(() => ({ data: {} })),
          axios.get(`${BACKEND_URL}/api/ledger/summary`).catch(() => ({ data: {} })),
          axios.get(`${BACKEND_URL}/api/compliance/deadlines`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/accounting/reports/summary`).catch(() => ({ data: {} })),
        ]);
        if (!alive) return;
        setInvoices(Array.isArray(inv.data) ? inv.data : []);
        setReceipts(rec.data?.rows || []);
        setLinks(lk.data?.items || []);
        setLedger(led.data?.totals || { receivable: 0, payable: 0, net: 0 });
        setDeadlines(Array.isArray(dl.data) ? dl.data : (dl.data?.deadlines || []));
        setMarketingSpend(summary.data?.byCategory?.marketing || null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const outstanding = useMemo(() => invoices.filter(isOutstanding), [invoices]);
  const totalOutstanding = useMemo(
    () => outstanding.reduce((s, i) => s + invoiceOutstanding(i), 0), [outstanding]);

  const monthKey = new Date().toISOString().slice(0, 7);
  const receiptsThisMonth = useMemo(
    () => receipts.filter((r) => String(r.date || r.createdAt || '').slice(0, 7) === monthKey), [receipts, monthKey]);
  const receiptsMonthTotal = useMemo(
    () => receiptsThisMonth.reduce((s, r) => s + Number(r.amount || 0), 0), [receiptsThisMonth]);

  const pendingLinks = useMemo(
    () => links.filter((l) => !CLOSED_LINK.includes((l.status || '').toLowerCase())), [links]);

  const taxDeadlines = useMemo(
    () => deadlines.filter((d) => /gst|tcs/i.test(d.tax_type || '')).slice(0, 6), [deadlines]);

  const topOutstanding = useMemo(
    () => [...outstanding].sort((a, b) => invoiceOutstanding(b) - invoiceOutstanding(a)).slice(0, 8), [outstanding]);
  const recentReceipts = useMemo(
    () => [...receipts].sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))).slice(0, 8), [receipts]);

  if (loading) {
    return (
      <div className="flex justify-center py-24" data-testid="accounts-dashboard-loading">
        <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl" data-testid="accounts-dashboard">
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900 flex items-center gap-3">
          <IndianRupee className="h-7 w-7 text-teal-600" />Accounts Dashboard
        </h1>
        <p className="text-slate-500 mt-1">Collections, payables and compliance at a glance.</p>
      </div>

      {/* Recon Auto-Watch — reconciliation flag count → full report. */}
      <ReconWatchChip />
      <TallyExportReminder />

      <Link to="/reports/reconstructed-accounts" data-testid="link-reconstructed-accounts"
        className="flex items-center justify-between px-4 py-3 rounded-xl bg-teal-50 border border-teal-100 hover:bg-teal-100 transition-colors">
        <span className="text-sm font-semibold text-teal-800">Reconstructed Accounts — historical exceptions, financial position &amp; growth</span>
        <ArrowUpRight className="h-4 w-4 text-teal-600" />
      </Link>


      {/* KPI strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testid="kpi-outstanding" icon={IndianRupee} tone="amber" label="Total Outstanding" value={inr(totalOutstanding)} sub={`${outstanding.length} open invoice${outstanding.length !== 1 ? 's' : ''}`} />
        <KpiCard testid="kpi-unpaid-count" icon={FileText} tone="rose" label="Unpaid Invoices" value={outstanding.length} sub="awaiting payment" />
        <KpiCard testid="kpi-receipts-month" icon={Receipt} tone="teal" label="Receipts This Month" value={inr(receiptsMonthTotal)} sub={`${receiptsThisMonth.length} receipt${receiptsThisMonth.length !== 1 ? 's' : ''}`} />
        <KpiCard testid="kpi-pending-links" icon={CreditCard} tone="sky" label="Pending Payment Links" value={pendingLinks.length} sub="active / unpaid" />
      </div>

      {/* Quick Marketing Spend widget — accounts-only page, reuses the existing
          expenses-by-category aggregation (no new backend). */}
      {marketingSpend && marketingSpend.count > 0 && (
        <Card data-testid="marketing-spend-widget" className="p-4 bg-white rounded-2xl border border-slate-100 shadow-sm flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center text-white shrink-0">
            <Megaphone className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Marketing Spend (All-Time)</div>
            <div className="text-xl font-bold text-slate-900 leading-tight" data-testid="marketing-spend-total">{inr(marketingSpend.total)}</div>
          </div>
          <div className="text-xs text-slate-400 shrink-0">{marketingSpend.count} expense{marketingSpend.count !== 1 ? 's' : ''}</div>
        </Card>
      )}

      {/* Row 1 — Outstanding + Recent receipts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <Panel testid="panel-outstanding" title="Outstanding / Payments Due" icon={AlertCircle}
          action={<Link to="/invoices" className="text-xs font-semibold text-teal-600 flex items-center gap-1">View all<ArrowUpRight className="h-3 w-3" /></Link>}>
          {topOutstanding.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">No outstanding invoices</p>
          ) : (
            <div className="space-y-2">
              {topOutstanding.map((inv) => (
                <Link key={inv.invoiceNumber} to="/invoices" data-testid={`outstanding-row-${inv.invoiceNumber}`}
                  className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-50 transition-colors">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{inv.client?.name || inv.client?.displayUCC || 'Client'}</div>
                    <div className="text-[11px] text-slate-400 truncate">{inv.invoiceNumber} · {inv.status}</div>
                  </div>
                  <div className="text-sm font-semibold text-amber-600 shrink-0">{inr(invoiceOutstanding(inv))}</div>
                </Link>
              ))}
            </div>
          )}
        </Panel>

        <Panel testid="panel-receipts" title="Recent Receipts" icon={Receipt}
          action={<Link to="/finance/receipts" className="text-xs font-semibold text-teal-600 flex items-center gap-1">All receipts<ArrowUpRight className="h-3 w-3" /></Link>}>
          {recentReceipts.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">No receipts yet</p>
          ) : (
            <div className="space-y-2">
              {recentReceipts.map((r) => (
                <div key={r.receiptId} data-testid={`receipt-row-${r.receiptId}`}
                  className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-50 transition-colors">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{r.traveller || r.clientUCC || '—'}</div>
                    <div className="text-[11px] text-slate-400 truncate">{r.date} · {(r.method || '').toUpperCase()}</div>
                  </div>
                  <div className="text-sm font-semibold text-emerald-600 shrink-0">{inr(r.amount)}</div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {/* Row 2 — Ledger + Tax status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <Panel testid="panel-ledger" title="Ledger Summary" icon={BookOpen}
          action={<Link to="/ledger" className="text-xs font-semibold text-teal-600 flex items-center gap-1">Open ledger<ArrowUpRight className="h-3 w-3" /></Link>}>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 rounded-xl bg-emerald-50">
              <div className="text-[11px] uppercase tracking-wide text-emerald-600 font-semibold">Receivable</div>
              <div className="text-lg font-bold text-emerald-700">{inr(ledger.receivable)}</div>
            </div>
            <div className="text-center p-3 rounded-xl bg-rose-50">
              <div className="text-[11px] uppercase tracking-wide text-rose-600 font-semibold">Payable</div>
              <div className="text-lg font-bold text-rose-700">{inr(ledger.payable)}</div>
            </div>
            <div className="text-center p-3 rounded-xl bg-slate-50">
              <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Net</div>
              <div className="text-lg font-bold text-slate-800">{inr(ledger.net)}</div>
            </div>
          </div>
        </Panel>

        <Panel testid="panel-tax-status" title="GST / TCS Filing Status" icon={CalendarClock}
          action={<Link to="/compliance" className="text-xs font-semibold text-teal-600 flex items-center gap-1">Tax calendar<ArrowUpRight className="h-3 w-3" /></Link>}>
          {taxDeadlines.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">No upcoming GST/TCS deadlines</p>
          ) : (
            <div className="space-y-2">
              {taxDeadlines.map((d) => (
                <div key={d.id} data-testid={`tax-deadline-${d.id}`} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-50">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{d.description || d.tax_type}</div>
                    <div className="text-[11px] text-slate-400 truncate">{d.period_label} · due {d.due_date}</div>
                  </div>
                  <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full shrink-0 ${taxStatusColor(d.status)}`}>{d.status || 'pending'}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {/* Row 3 — Pending payment links */}
      <Panel testid="panel-payment-links" title="Pending Payment Links" icon={CreditCard}
        action={<Link to="/payment-links" className="text-xs font-semibold text-teal-600 flex items-center gap-1">Manage<ArrowUpRight className="h-3 w-3" /></Link>}>
        {pendingLinks.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">No pending payment links</p>
        ) : (
          <div className="space-y-2">
            {pendingLinks.slice(0, 8).map((l) => (
              <div key={l.linkId} data-testid={`link-row-${l.linkId}`} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-50">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{l.customerName || l.description || 'Payment'}</div>
                  <div className="text-[11px] text-slate-400 truncate">{String(l.createdAt || '').slice(0, 10)} · {l.status}</div>
                </div>
                <div className="text-sm font-semibold text-sky-600 shrink-0">{inr(l.amount)}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
};

export default AccountsDashboard;
